import { Hotel, HotelFormData } from '../types/hotel';
import { get, post, put, del } from '../utils/httpClient';

// --- Dashboard Types ---

export interface GlobalDashboardStats {
  total_hotels: number;
  active_hotels: number;
  pending_hotels: number;
  total_rooms: number;
  occupied_rooms: number;
  global_occupancy_rate: number;
  total_fpt_revenue: number;
  fpt_total_due: number;
  fpt_paid_count: number;
  fpt_overdue_count: number;
  checkins_today: number;
  checkins_this_week: number;
  checkins_this_month: number;
  hotels_count?: number;
  restaurants_count?: number;
  lounges_count?: number;
  attractions_count?: number;
  hotel_rooms_total?: number;
}

export interface TrendDataPoint {
  date: string;
  checkins: number;
  occupancy_rate: number;
}

export interface DashboardTrends {
  period: string;
  data_points: TrendDataPoint[];
}

// --- KYC / Onboarding Types ---

export interface KYCDocumentView {
  id: string;
  hotel_id: string;
  document_type: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  status: 'submitted' | 'approved' | 'rejected';
  submitted_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  rejection_reason: string | null;
}

export interface HotelKYCView {
  hotel_id: string;
  hotel_name: string;
  kyc_status: string;
  documents: KYCDocumentView[];
  rejection_reason: string | null;
}

export type SectorKey = 'HOTEL' | 'RESTAURANT' | 'OTHER' | 'TOURISM';

export interface HotelSearchParams {
  search?: string;
  province?: string;
  category?: number;
  status?: 'active' | 'suspended' | 'pending';
  sector?: SectorKey | string;
  kyc_status?: string;
  is_active?: boolean;
  registered_after?: string;
  registered_before?: string;
  sort_by?: string;
  sort_order?: 'ASC' | 'DESC';
  limit?: number;
  offset?: number;
}

export function normalizeSector(value?: string | null): SectorKey {
  const s = String(value || 'HOTEL').toUpperCase().trim();
  if (s === 'HOTEL' || s === 'RESTAURANT' || s === 'OTHER' || s === 'TOURISM') return s;
  if (s === 'RESTO' || s === 'CAFE' || s === 'CAFÉ') return 'RESTAURANT';
  if (s === 'BAR' || s === 'LOUNGE') return 'OTHER';
  if (s === 'ATTRACTION' || s === 'TOURISTIQUE') return 'TOURISM';
  return 'HOTEL';
}

export const SECTOR_META: Record<SectorKey, { label: string; short: string; capacity: string }> = {
  HOTEL: { label: 'Hôtels', short: 'Hôtel', capacity: 'Chambres' },
  RESTAURANT: { label: 'Restaurants & Cafés', short: 'Restaurant', capacity: 'Places' },
  OTHER: { label: 'Lounges & Bars', short: 'Lounge', capacity: 'Places' },
  TOURISM: { label: 'Attractions', short: 'Attraction', capacity: 'Capacité' },
};

export interface HotelDetailStats {
  total_guests: number;
  current_occupancy: number;
  total_rooms: number;
  occupancy_rate: number;
  checkins_today: number;
  checkins_this_month: number;
  average_stay_duration: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
  total_pages: number;
}

function buildQueryString(params: HotelSearchParams): string {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.province) query.set('province', params.province);
  if (params.category) query.set('category', String(params.category));
  if (params.status) query.set('status', params.status);
  if (params.sector) query.set('sector', params.sector);
  if (params.kyc_status) query.set('kyc_status', params.kyc_status);
  if (params.is_active !== undefined) query.set('is_active', String(params.is_active));
  if (params.registered_after) query.set('registered_after', params.registered_after);
  if (params.registered_before) query.set('registered_before', params.registered_before);
  if (params.sort_by) query.set('sort_by', params.sort_by);
  if (params.sort_order) query.set('sort_order', params.sort_order);
  if (params.limit) query.set('limit', String(Math.min(params.limit, 100)));
  if (params.offset !== undefined) query.set('offset', String(params.offset));
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

async function parseErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json();
    return data.error || data.message || fallback;
  } catch {
    return fallback;
  }
}

/** Fetch hotels with optional search/filter/pagination */
export async function fetchHotels(params: HotelSearchParams = {}): Promise<PaginatedResponse<Hotel>> {
  const qs = buildQueryString({ limit: 50, ...params });
  const response = await get(`/api/v1/admin/hotels${qs}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la récupération des hôtels');
    throw new Error(msg);
  }
  return response.json();
}

/** Create a new hotel */
export async function createHotel(data: HotelFormData): Promise<Hotel> {
  const response = await post('/api/v1/admin/hotels', data);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors de la création de l'hôtel");
    throw new Error(msg);
  }
  return response.json();
}

/** Update an existing hotel */
export async function updateHotel(id: string, data: Partial<HotelFormData>): Promise<Hotel> {
  const response = await put(`/api/v1/admin/hotels/${id}`, data);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors de la mise à jour de l'hôtel");
    throw new Error(msg);
  }
  return response.json();
}

/** Suspend a hotel */
export async function suspendHotel(id: string, reason: string): Promise<void> {
  const response = await post(`/api/v1/admin/hotels/${id}/suspend`, { reason });
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors de la suspension de l'hôtel");
    throw new Error(msg);
  }
}

/** Activate a hotel */
export async function activateHotel(id: string): Promise<void> {
  const response = await post(`/api/v1/admin/hotels/${id}/activate`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors de l'activation de l'hôtel");
    throw new Error(msg);
  }
}

/** Delete a hotel */
export async function deleteHotel(id: string): Promise<void> {
  const response = await del(`/api/v1/admin/hotels/${id}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors de la suppression de l'hôtel");
    throw new Error(msg);
  }
}

/** Fetch global hotel statistics */
export async function fetchHotelStatistics(): Promise<{
  total_hotels: number;
  active_hotels: number;
  total_guests: number;
  current_occupancy: number;
  total_rooms: number;
  average_occupancy_rate: number;
}> {
  const response = await get('/api/v1/admin/hotels/statistics');
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la récupération des statistiques');
    throw new Error(msg);
  }
  return response.json();
}

/** Fetch per-hotel statistics */
export async function fetchHotelDetailStats(hotelId: string): Promise<HotelDetailStats> {
  const response = await get(`/api/v1/admin/hotels/${hotelId}/statistics`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la récupération des statistiques');
    throw new Error(msg);
  }
  return response.json();
}

/** Fetch a single hotel by ID */
export async function fetchHotelById(hotelId: string): Promise<Hotel> {
  const response = await get(`/api/v1/admin/hotels/${hotelId}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors de la récupération de l'hôtel");
    throw new Error(msg);
  }
  return response.json();
}

/** Bulk suspend hotels */
export async function bulkSuspendHotels(ids: string[], reason: string): Promise<void> {
  for (const id of ids) {
    await suspendHotel(id, reason);
  }
}

/** Bulk activate hotels */
export async function bulkActivateHotels(ids: string[]): Promise<void> {
  for (const id of ids) {
    await activateHotel(id);
  }
}

/** Bulk delete hotels */
export async function bulkDeleteHotels(ids: string[]): Promise<void> {
  for (const id of ids) {
    await deleteHotel(id);
  }
}


// --- Dashboard API ---

/** Fetch global dashboard statistics, optionally filtered by province */
export async function fetchGlobalDashboardStats(province?: string): Promise<GlobalDashboardStats> {
  const query = province ? `?province=${encodeURIComponent(province)}` : '';
  const response = await get(`/api/v1/admin/dashboard/global-stats${query}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la récupération des statistiques globales');
    throw new Error(msg);
  }
  const body = await response.json();
  // Support both raw stats and { data: stats } envelopes
  return (body.data || body) as GlobalDashboardStats;
}

/** Fetch dashboard trends (30 days), optionally filtered by province */
export async function fetchDashboardTrends(province?: string): Promise<DashboardTrends> {
  const query = province ? `?province=${encodeURIComponent(province)}` : '';
  const response = await get(`/api/v1/admin/dashboard/trends${query}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la récupération des tendances');
    throw new Error(msg);
  }
  return response.json();
}

// --- Onboarding API ---

/** Fetch KYC status and documents for a specific hotel */
export async function fetchHotelKYC(hotelId: string): Promise<HotelKYCView> {
  const response = await get(`/api/v1/admin/hotels/${hotelId}/kyc`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la récupération des documents KYC');
    throw new Error(msg);
  }
  return response.json();
}

/** Approve a hotel onboarding */
export async function approveHotel(hotelId: string): Promise<void> {
  const response = await post(`/api/v1/admin/hotels/${hotelId}/approve`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors de l'approbation de l'hôtel");
    throw new Error(msg);
  }
}

/** Reject a hotel onboarding with a reason */
export async function rejectHotel(hotelId: string, reason: string): Promise<void> {
  const response = await post(`/api/v1/admin/hotels/${hotelId}/reject`, { reason });
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors du rejet de l'hôtel");
    throw new Error(msg);
  }
}
